import { useEffect, useMemo, useRef, useState } from 'react';

import { getView } from '../../engine/prism.js';
import { partAt, resolvePartPath, setOps, type ModelDoc } from '../../lib/model-doc.js';
import { pointer, registerPie } from '../../lib/pies.js';
import { dispatchShortcut, keyHint, registerCommands, useCommands, useShortcutContext, useShortcutVersion, type CommandSpec } from '../../lib/shortcuts/index.js';
import { pixelThumb, rawUrl } from '../../lib/thumbnails.js';
import {
  alignUvs,
  densityOf,
  editSeams,
  EMPTY_SELECTION,
  flipUvs,
  invertSelection,
  isMeshShape,
  layoutFromMesh,
  layoutSignature,
  makeSelection,
  pick,
  pivotOf,
  revalidateSelection,
  seamsFromIslands,
  selectAll,
  selectedEdges,
  selectedUvIndices,
  selectLinked,
  setPins,
  setTexelDensity,
  texelsPerMeter,
  transformUvs,
  vertexEdgesOf,
  withMeshUvs,
  type MeshShape,
  type MeshUvPatch,
  type UvLayout,
  type UvSelectMode,
  type Vec2,
} from '../../lib/uv-doc.js';
import { useLayout } from '../../state/layout.js';
import { useModel } from '../../state/model.js';
import { useProjectFiles } from '../../state/project-files.js';
import { useUv, type StretchOverlay, type UvBackground } from '../../state/uv.js';
import { useWorkspace } from '../../state/workspace.js';
import { Card, ContextMenu, EmptyState, MenuButton, NumberField, PropRow, type MenuItem } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { opCommand, opParams, UV_OPS, uvOp, uvOpLabel, type UvOpId, type UvParamValue } from './uv-ops.js';
import { UvOpDialog } from './UvOpDialog.js';
import { UvViewport, type UvViewportApi } from './UvViewport.js';
import './uv.css';

/**
 * UV Editor (Blender's UV Editing workspace): the selected model part's UVs
 * over its base-color texture or a checker, with vertex / edge / face / island
 * selection, G / R / S transforms, pins, seams, alignment, texel density, and
 * the engine's unwrap / projection / packing operators. Edits go through
 * `useModel.update`, one undo step each; engine operators come back as
 * `modelChanged`.
 */

export const UV_CONTEXT = 'model.uv';

/** Where the editor's UVs come from for the selected part. */
export type UvSource =
  | { kind: 'none'; message: string }
  | { kind: 'mesh'; path: string; index: number[]; shape: MeshShape; layout: UvLayout | null }
  | { kind: 'generated'; path: string; index: number[]; layout: UvLayout | null };

export function uvSource(doc: ModelDoc | null, selected: string | null, engine: UvLayout | null, overlap: boolean): UvSource {
  if (!doc) return { kind: 'none', message: 'Open a model to edit its UVs.' };
  if (!selected) return { kind: 'none', message: 'Select a part in the Model view to see its UVs.' };
  const index = resolvePartPath(doc, selected);
  const part = index ? partAt(doc, index) : null;
  if (!index || !part) return { kind: 'none', message: 'Select a part in the Model view to see its UVs.' };
  if (!part.shape || part.shape.type === 'empty') return { kind: 'none', message: `${selected} has no geometry.` };
  if (isMeshShape(part.shape)) return { kind: 'mesh', path: selected, index, shape: part.shape, layout: layoutFromMesh(part.shape, selected, { overlap }) };
  return { kind: 'generated', path: selected, index, layout: engine && engine.path === selected ? engine : null };
}

const MODES: { mode: UvSelectMode; label: string; key: string }[] = [
  { mode: 'vertex', label: 'Vertex', key: '1' },
  { mode: 'edge', label: 'Edge', key: '2' },
  { mode: 'face', label: 'Face', key: '3' },
  { mode: 'island', label: 'Island', key: '4' },
];

/** Base-color image of the part's material (`map`), loaded for the background. */
function useTexture(doc: ModelDoc | null, src: UvSource): { image: HTMLImageElement | null; size: Vec2 | null; path: string | null } {
  const projectId = useWorkspace((s) => s.projectId);
  const part = src.kind === 'none' || !doc ? null : partAt(doc, src.index);
  const material = part ? doc?.materials?.[part.material ?? 'default'] : undefined;
  const path = typeof material?.map === 'string' && material.map ? material.map : null;
  const version = useProjectFiles((s) => (path ? (s.entries.find((e) => e.path === path)?.modifiedAt ?? '') : ''));
  const [loaded, setLoaded] = useState<{ key: string; image: HTMLImageElement } | null>(null);
  const key = `${projectId}:${path}:${version}`;

  useEffect(() => {
    if (!projectId || !path || typeof Image === 'undefined') return;
    let cancelled = false;
    void (async () => {
      const url = path.endsWith('.pixel.json') ? await pixelThumb(projectId, path, version) : rawUrl(projectId, path, version);
      if (!url || cancelled) return;
      const image = new Image();
      image.onload = () => {
        if (!cancelled) setLoaded({ key, image });
      };
      image.src = url;
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, path, version, key]);

  const image = loaded?.key === key ? loaded.image : null;
  return { image, size: image ? [image.naturalWidth, image.naturalHeight] : null, path };
}

function isPanelVisible(panel: string): boolean {
  const el = document.querySelector(`[data-dock-panel="${panel}"]`) as HTMLElement | null;
  return Boolean(el?.isConnected && el.offsetParent !== null);
}

const fmt = (n: number, digits = 3) => {
  const r = Number(n.toFixed(digits));
  return Object.is(r, -0) ? '0' : String(r);
};

export function UvEditor() {
  const doc = useModel((s) => s.doc);
  const selected = useModel((s) => s.selected);
  const engineLayout = useUv((s) => s.engineLayout);
  const overlays = useUv((s) => s.overlays);
  const mode = useUv((s) => s.mode);
  const selection = useUv((s) => s.selection);
  const sidebar = useUv((s) => s.sidebar);
  const status = useUv((s) => s.status);
  const modal = useUv((s) => s.modal);
  const src = useMemo(() => uvSource(doc, selected, engineLayout, overlays.overlap), [doc, selected, engineLayout, overlays.overlap]);
  const layout = src.kind === 'none' ? null : src.layout;
  const editable = src.kind === 'mesh' && Boolean(layout);
  const texture = useTexture(doc, src);
  const apiRef = useRef<UvViewportApi | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; items: MenuItem[] } | null>(null);
  const [dialog, setDialog] = useState<UvOpId | null>(null);
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  useShortcutVersion();
  useShortcutContext(UV_CONTEXT, hover || focus);

  // The selection belongs to one part and survives edits that keep the UV topology.
  const signature = useRef<string | null>(null);
  useEffect(() => {
    const st = useUv.getState();
    if (!layout) return;
    if (st.selectionPath !== layout.path) {
      signature.current = layoutSignature(layout);
      st.setSelection(layout.path, EMPTY_SELECTION);
      st.setStatus(null);
      return;
    }
    const sig = layoutSignature(layout);
    if (sig === signature.current) return;
    signature.current = sig;
    st.setSelection(layout.path, revalidateSelection(layout, st.mode, st.selection));
  }, [layout]);

  // Non-mesh parts: ask the engine for the generated UVs whenever the document changes.
  const generatedPath = src.kind === 'generated' ? src.path : null;
  useEffect(() => {
    if (!generatedPath) return;
    const timer = setTimeout(() => useUv.getState().requestLayout(generatedPath), 150);
    return () => clearTimeout(timer);
  }, [generatedPath, doc]);

  const textureSize = useUv((s) => s.textureSize) ?? texture.size?.[0] ?? 1024;
  const targetDensity = useUv((s) => s.targetDensity);

  /* ------------------------------ actions ------------------------------ */

  const latest = useRef({ src, layout, editable, textureSize });
  latest.current = { src, layout, editable, textureSize };

  const actions = useMemo(() => {
    const st = () => useUv.getState();
    const status = (message: string | null) => st().setStatus(message);

    /** Write UV fields of the mesh part (one undo step). */
    const commit = (patch: MeshUvPatch) => {
      const { src: s } = latest.current;
      const model = useModel.getState();
      if (s.kind !== 'mesh' || !model.doc) return;
      const next = withMeshUvs(model.doc, s.index, patch);
      if (next !== model.doc) model.update(next);
    };

    /** The editable layout, or null with the reason in the status line. */
    const editableLayout = (): UvLayout | null => {
      const { src: s, layout: l } = latest.current;
      if (s.kind === 'generated') {
        status('Generated UVs are read-only: Convert to Mesh, or add an Unwrap op to the part.');
        return null;
      }
      if (s.kind !== 'mesh') return null;
      if (!l) {
        status('This mesh has no UVs yet: unwrap it first (U).');
        return null;
      }
      return l;
    };

    /** Edit the selected UVs with `fn`; nothing selected = a hint. */
    const editSelected = (fn: (l: UvLayout, indices: number[]) => Vec2[]) => {
      const l = editableLayout();
      if (!l) return;
      const indices = selectedUvIndices(st().selection);
      if (!indices.length) {
        status('Nothing selected.');
        return;
      }
      status(null);
      commit({ uvs: fn(l, indices) });
    };

    const select = (fn: (l: UvLayout, m: UvSelectMode) => ReturnType<typeof selectAll>) => {
      const l = latest.current.layout;
      if (!l) return;
      st().setSelection(l.path, fn(l, st().mode));
    };

    const selectedFaces = (l: UvLayout) => {
      const faces = [...st().selection.faces];
      return faces.length ? faces : l.faces.map((_, f) => f);
    };

    return {
      setMode: (m: UvSelectMode) => st().setMode(m, latest.current.layout),
      selectAll: () => select((l, m) => selectAll(l, m)),
      selectNone: () => select((l, m) => makeSelection(l, m, {})),
      invert: () => select((l, m) => invertSelection(l, m, st().selection)),
      linked: () => select((l, m) => selectLinked(l, m, st().selection)),
      /** L: the island under the mouse joins the selection. */
      linkedPick: () => {
        const l = latest.current.layout;
        const at = apiRef.current?.pointerUv();
        if (!l || !at) return;
        const hit = pick(l, at, 'face', 10 / st().view.zoom);
        if (hit?.kind !== 'face') return;
        const island = selectLinked(l, 'face', makeSelection(l, 'face', { faces: [hit.index] }));
        const cur = st().selection;
        const m = st().mode;
        st().setSelection(
          l.path,
          m === 'face' || m === 'island'
            ? makeSelection(l, m, { faces: [...cur.faces, ...island.faces] })
            : makeSelection(l, m, { uvs: [...cur.uvs, ...island.uvs] }),
        );
      },
      selectPinned: () => select((l, m) => makeSelection(l, m, { uvs: l.pins })),
      selectOverlap: () => {
        const { layout: l, src: s } = latest.current;
        if (!l) return;
        // Overlaps are only computed while their overlay is on: check now when it is off.
        const overlap = st().overlays.overlap || s.kind !== 'mesh' ? l.overlap : (layoutFromMesh(s.shape, l.path, { overlap: true })?.overlap ?? []);
        st().setOverlays({ overlap: true });
        st().setSelection(l.path, makeSelection(l, st().mode, { faces: overlap }));
      },
      transform: (kind: 'grab' | 'rotate' | 'scale') => {
        if (!editableLayout()) return;
        apiRef.current?.startTransform(kind);
      },
      move: (du: number, dv: number) => editSelected((l, i) => transformUvs(l.uvs, i, { kind: 'translate', delta: [du, dv] })),
      rotate: (deg: number) => editSelected((l, i) => transformUvs(l.uvs, i, { kind: 'rotate', angle: (deg * Math.PI) / 180, pivot: pivotOf(l.uvs, i) })),
      scale: (su: number, sv: number) => editSelected((l, i) => transformUvs(l.uvs, i, { kind: 'scale', scale: [su, sv], pivot: pivotOf(l.uvs, i) })),
      flip: (axis: 'u' | 'v') => editSelected((l, i) => flipUvs(l.uvs, i, axis)),
      align: (axis: 'u' | 'v', how: 'min' | 'max' | 'center') => editSelected((l, i) => alignUvs(l.uvs, i, axis, how)),
      pin: (on: boolean) => {
        const l = editableLayout();
        if (!l) return;
        const indices = selectedUvIndices(st().selection);
        if (!indices.length) return status('Nothing selected.');
        status(null);
        commit({ uvPins: setPins(l.pins, indices, on) });
      },
      seam: (clear: boolean) => {
        const l = editableLayout();
        const s = latest.current.src;
        if (!l || s.kind !== 'mesh') return;
        const edges = vertexEdgesOf(l, selectedEdges(l, st().mode, st().selection));
        if (!edges.length) return status('Select the edges to mark (edge mode, 2).');
        status(null);
        commit({ seams: editSeams(s.shape.seams ?? [], edges, clear) });
      },
      seamsFromIslands: () => {
        const l = editableLayout();
        if (!l) return;
        status(null);
        commit({ seams: seamsFromIslands(l) });
      },
      setDensity: () => {
        const l = editableLayout();
        if (!l) return;
        const { textureSize: size } = latest.current;
        const target = st().targetDensity;
        if (!(target > 0) || !(size > 0)) return;
        status(null);
        commit({ uvs: setTexelDensity(l, selectedFaces(l), target / size) });
      },
      /** Take the selection's current density as the target. */
      sampleDensity: () => {
        const l = latest.current.layout;
        if (!l) return;
        const d = l.faceArea3d.length ? densityOf(l, selectedFaces(l)) : l.texelDensity;
        st().setTargetDensity(Math.round(texelsPerMeter(d, latest.current.textureSize)));
      },
      /** Run an engine operator (asking its options first when it has some). */
      op: (id: UvOpId, params?: Record<string, UvParamValue>) => {
        const s = latest.current.src;
        const def = uvOp(id);
        if (s.kind === 'none') return status(s.message);
        if (s.kind === 'generated') {
          status('Generated UVs are read-only: Convert to Mesh, or add an Unwrap op to the part.');
          return;
        }
        if (def.fields && !params) {
          setDialog(id);
          return;
        }
        const sel = st().selection;
        if (def.needsSelection && sel.uvs.size === 0) return status(`${def.label}: select UVs first.`);
        if (!getView('model')) return status('UV operators run in the engine: open the Model view first.');
        if (params) st().setParams(id, params);
        st().runOp(def.op, s.path, opCommand(def, params ?? {}, { faces: [...sel.faces].sort((a, b) => a - b), uvs: selectedUvIndices(sel) }));
      },
      frame: (selectedOnly: boolean) => apiRef.current?.frame(selectedOnly),
      convertToMesh: () => useModel.getState().convertToMesh(),
      addUnwrapOp: () => {
        const s = latest.current.src;
        const model = useModel.getState();
        if (s.kind !== 'generated' || !model.doc) return;
        const part = partAt(model.doc, s.index);
        model.update(setOps(model.doc, s.index, [...(part?.ops ?? []), { op: 'unwrap', method: 'smart' }]));
      },
    };
  }, []);

  /* ----------------------------- shortcuts ----------------------------- */

  useEffect(() => {
    const opCommands: CommandSpec[] = (Object.keys(UV_OPS) as UvOpId[]).map((id) => ({
      id: `uv.op.${id}`,
      label: uvOpLabel(id),
      category: 'UV',
      context: UV_CONTEXT,
      keywords: `uv ${uvOp(id).keywords ?? ''}`,
      keys: OP_KEYS[id] ?? [],
      enabled: () => latest.current.src.kind === 'mesh',
      run: () => actions.op(id),
    }));
    return registerCommands(opCommands);
  }, [actions]);

  useCommands(
    [
      ...MODES.map(({ mode: m, label, key }) => ({ id: `uv.mode.${m}`, label: `${label} select mode`, category: 'UV', context: UV_CONTEXT, keys: [key], run: () => actions.setMode(m) })),
      { id: 'uv.select.all', label: 'Select all', category: 'UV', context: UV_CONTEXT, keys: ['A'], run: actions.selectAll },
      { id: 'uv.select.none', label: 'Select none', category: 'UV', context: UV_CONTEXT, keys: ['Alt+A'], run: actions.selectNone },
      { id: 'uv.select.invert', label: 'Invert selection', category: 'UV', context: UV_CONTEXT, keys: ['Ctrl+I'], run: actions.invert },
      { id: 'uv.select.linked', label: 'Select linked', category: 'UV', context: UV_CONTEXT, keys: ['Ctrl+L'], run: actions.linked },
      { id: 'uv.select.linked-pick', label: 'Select linked under cursor', category: 'UV', context: UV_CONTEXT, keys: ['L'], run: actions.linkedPick },
      { id: 'uv.select.pinned', label: 'Select pinned', category: 'UV', context: UV_CONTEXT, keys: ['Shift+P'], run: actions.selectPinned },
      { id: 'uv.select.overlap', label: 'Select overlapping', category: 'UV', context: UV_CONTEXT, run: actions.selectOverlap },
      { id: 'uv.grab', label: 'Move UVs', category: 'UV', context: UV_CONTEXT, keys: ['G'], run: () => actions.transform('grab') },
      { id: 'uv.rotate', label: 'Rotate UVs', category: 'UV', context: UV_CONTEXT, keys: ['R'], run: () => actions.transform('rotate') },
      { id: 'uv.scale', label: 'Scale UVs', category: 'UV', context: UV_CONTEXT, keys: ['S'], run: () => actions.transform('scale') },
      { id: 'uv.flip-u', label: 'Flip horizontally (U)', category: 'UV', context: UV_CONTEXT, run: () => actions.flip('u') },
      { id: 'uv.flip-v', label: 'Flip vertically (V)', category: 'UV', context: UV_CONTEXT, run: () => actions.flip('v') },
      { id: 'uv.rotate-ccw', label: 'Rotate 90° counter-clockwise', category: 'UV', context: UV_CONTEXT, run: () => actions.rotate(90) },
      { id: 'uv.rotate-cw', label: 'Rotate 90° clockwise', category: 'UV', context: UV_CONTEXT, run: () => actions.rotate(-90) },
      { id: 'uv.pin', label: 'Pin', category: 'UV', context: UV_CONTEXT, keys: ['P'], run: () => actions.pin(true) },
      { id: 'uv.unpin', label: 'Unpin', category: 'UV', context: UV_CONTEXT, keys: ['Alt+P'], run: () => actions.pin(false) },
      { id: 'uv.align', label: 'Align…', category: 'UV', context: UV_CONTEXT, keys: ['Shift+W'], run: () => openMenuAtPointer(alignItems()) },
      { id: 'uv.seam.mark', label: 'Mark seam', category: 'UV', context: UV_CONTEXT, run: () => actions.seam(false) },
      { id: 'uv.seam.clear', label: 'Clear seam', category: 'UV', context: UV_CONTEXT, run: () => actions.seam(true) },
      { id: 'uv.seam.from-islands', label: 'Seams from islands', category: 'UV', context: UV_CONTEXT, run: actions.seamsFromIslands },
      { id: 'uv.texel.set', label: 'Set texel density', category: 'UV', context: UV_CONTEXT, run: actions.setDensity },
      { id: 'uv.frame-all', label: 'Frame all', category: 'UV', context: UV_CONTEXT, keys: ['Home'], run: () => actions.frame(false) },
      { id: 'uv.frame-selected', label: 'Frame selected', category: 'UV', context: UV_CONTEXT, keys: ['Num.'], run: () => actions.frame(true) },
      { id: 'uv.sidebar', label: 'Toggle UV sidebar', category: 'UV', context: UV_CONTEXT, keys: ['N'], run: () => useUv.getState().toggleSidebar() },
      { id: 'uv.checker', label: 'Toggle checker background', category: 'UV', context: UV_CONTEXT, run: () => toggleChecker() },
      { id: 'uv.menu', label: 'UV menu', category: 'UV', context: UV_CONTEXT, run: () => openMenuAtPointer(uvMenuItems()) },
    ],
    [actions],
  );

  // U: Blender's unwrap menu, as a pie.
  useEffect(
    () =>
      registerPie(
        {
          id: 'uv.unwrap',
          label: 'UV Unwrap',
          items: () =>
            (['unwrapAngle', 'unwrapConformal', 'smartProject', 'projectView', 'cube', 'cylinder', 'sphere', 'pack'] as UvOpId[]).map((id) => ({
              id,
              label: uvOp(id).label,
              commandId: `uv.op.${id}`,
              disabled: latest.current.src.kind !== 'mesh',
            })),
        },
        { keys: ['U'], context: UV_CONTEXT, label: 'Pie: UV Unwrap', category: 'UV' },
      ),
    [],
  );

  /* ------------------------------- menus ------------------------------- */

  function item(id: string, label: string, run: () => void, extra: Partial<MenuItem> = {}): MenuItem {
    return { id, label, run, shortcut: keyHint(id) || undefined, ...extra };
  }
  const opItem = (id: UvOpId): MenuItem =>
    item(`uv.op.${id}`, uvOpLabel(id), () => actions.op(id), { disabled: latest.current.src.kind !== 'mesh' });
  const sep = (id: string): MenuItem => ({ id, label: '', separator: true });

  function alignItems(): MenuItem[] {
    return [
      { id: 'h-u', label: 'Align U', heading: true },
      item('a-umin', 'Left (U min)', () => actions.align('u', 'min')),
      item('a-uc', 'Center (U)', () => actions.align('u', 'center')),
      item('a-umax', 'Right (U max)', () => actions.align('u', 'max')),
      { id: 'h-v', label: 'Align V', heading: true },
      item('a-vmin', 'Bottom (V min)', () => actions.align('v', 'min')),
      item('a-vc', 'Center (V)', () => actions.align('v', 'center')),
      item('a-vmax', 'Top (V max)', () => actions.align('v', 'max')),
    ];
  }

  function uvMenuItems(): MenuItem[] {
    return [
      opItem('unwrapAngle'),
      opItem('unwrapConformal'),
      opItem('smartProject'),
      sep('s1'),
      opItem('cube'),
      opItem('cylinder'),
      opItem('sphere'),
      opItem('projectView'),
      sep('s2'),
      opItem('pack'),
      opItem('averageScale'),
      opItem('minimizeStretch'),
      opItem('stitch'),
      sep('s3'),
      item('uv.seam.mark', 'Mark Seam', () => actions.seam(false)),
      item('uv.seam.clear', 'Clear Seam', () => actions.seam(true)),
      item('uv.seam.from-islands', 'Seams from Islands', actions.seamsFromIslands),
      sep('s4'),
      item('uv.pin', 'Pin', () => actions.pin(true)),
      item('uv.unpin', 'Unpin', () => actions.pin(false)),
    ];
  }

  function transformItems(): MenuItem[] {
    return [
      item('uv.grab', 'Move', () => actions.transform('grab')),
      item('uv.rotate', 'Rotate', () => actions.transform('rotate')),
      item('uv.scale', 'Scale', () => actions.transform('scale')),
      sep('t1'),
      item('uv.flip-u', 'Flip Horizontally', () => actions.flip('u')),
      item('uv.flip-v', 'Flip Vertically', () => actions.flip('v')),
      item('uv.rotate-ccw', 'Rotate 90° CCW', () => actions.rotate(90)),
      item('uv.rotate-cw', 'Rotate 90° CW', () => actions.rotate(-90)),
      sep('t2'),
      ...alignItems(),
    ];
  }

  function selectItems(): MenuItem[] {
    return [
      item('uv.select.all', 'All', actions.selectAll),
      item('uv.select.none', 'None', actions.selectNone),
      item('uv.select.invert', 'Invert', actions.invert),
      sep('x1'),
      item('uv.select.linked', 'Linked', actions.linked),
      item('uv.select.pinned', 'Pinned', actions.selectPinned),
      item('uv.select.overlap', 'Overlapping', actions.selectOverlap),
    ];
  }

  function viewItems(): MenuItem[] {
    const st = useUv.getState();
    const check = (on: boolean) => (on ? 'check' : undefined);
    const bg = (b: UvBackground, label: string): MenuItem => ({ id: `bg-${b}`, label, icon: check(st.overlays.background === b), run: () => st.setOverlays({ background: b }) });
    const stretch = (s: StretchOverlay, label: string): MenuItem => ({ id: `st-${s}`, label, icon: check(st.overlays.stretch === s), run: () => st.setOverlays({ stretch: s }) });
    return [
      item('uv.frame-all', 'Frame All', () => actions.frame(false)),
      item('uv.frame-selected', 'Frame Selected', () => actions.frame(true)),
      { id: 'zoom-1', label: 'Zoom 1:1 (texture pixels)', run: () => st.setView({ center: st.view.center, zoom: latest.current.textureSize / (globalThis.devicePixelRatio || 1) }) },
      item('uv.sidebar', 'Sidebar', st.toggleSidebar, { icon: check(st.sidebar) }),
      { id: 'h-bg', label: 'Background', heading: true },
      bg('image', 'Texture image'),
      bg('checker', 'Checker grid'),
      bg('none', 'None'),
      { id: 'h-ov', label: 'Overlays', heading: true },
      { id: 'grid', label: 'Grid', icon: check(st.overlays.grid), run: () => st.setOverlays({ grid: !st.overlays.grid }) },
      { id: 'faces', label: 'Faces', icon: check(st.overlays.faces), run: () => st.setOverlays({ faces: !st.overlays.faces }) },
      { id: 'seams', label: 'Seams', icon: check(st.overlays.seams), run: () => st.setOverlays({ seams: !st.overlays.seams }) },
      { id: 'overlap', label: 'Overlapping faces', icon: check(st.overlays.overlap), run: () => st.setOverlays({ overlap: !st.overlays.overlap }) },
      { id: 'h-st', label: 'Stretch', heading: true },
      stretch('off', 'Off'),
      stretch('angle', 'Angle'),
      stretch('area', 'Area'),
    ];
  }

  function contextItems(): MenuItem[] {
    return [...transformItems().slice(0, 8), sep('c1'), ...uvMenuItems()];
  }

  function openMenuAtPointer(items: MenuItem[]) {
    setMenu({ x: pointer.x, y: pointer.y, items });
  }

  function toggleChecker() {
    const st = useUv.getState();
    st.setOverlays({ background: st.overlays.background === 'checker' ? 'image' : 'checker' });
  }

  /* ------------------------------- render ------------------------------ */

  const selFaces = [...selection.faces];
  const densityFaces = selFaces.length ? selFaces : (layout?.faces.map((_, f) => f) ?? []);
  const density = layout ? (layout.faceArea3d.length ? densityOf(layout, densityFaces) : layout.texelDensity) : 0;
  const indices = useMemo(() => selectedUvIndices(selection), [selection]);
  const center = layout && indices.length ? pivotOf(layout.uvs, indices) : null;

  return (
    <div
      className="uv-editor"
      tabIndex={-1}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      onFocus={() => setFocus(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocus(false);
      }}
      onPointerDown={(e) => {
        const target = e.target as HTMLElement;
        if (!target.closest('input, select, textarea, button')) e.currentTarget.focus({ preventScroll: true });
        // The UV Editor edits the model: its undo and shortcuts follow the Model view.
        const ws = useWorkspace.getState();
        if (ws.view !== 'model' && isPanelVisible('model')) ws.setView('model');
      }}
      onKeyDown={(e) => {
        // Model view hidden (so its shortcut context is off): still run UV commands while focused here.
        if (!e.defaultPrevented && useWorkspace.getState().view !== 'model') dispatchShortcut(e.nativeEvent, [UV_CONTEXT]);
      }}
    >
      <div className="viewport-toolbar uv-toolbar">
        <div className="tb-group segmented" role="group" aria-label="Select mode">
          {MODES.map(({ mode: m, label }) => (
            <button
              key={m}
              type="button"
              className={`uv-mode${mode === m ? ' active' : ''}`}
              aria-pressed={mode === m}
              title={`${label} select mode (${keyHint(`uv.mode.${m}`)})`}
              onClick={() => actions.setMode(m)}
            >
              {label}
            </button>
          ))}
        </div>
        <MenuButton className="tb-btn" items={viewItems} title="View and overlays">
          View <Icon name="chevronDown" size={11} />
        </MenuButton>
        <MenuButton className="tb-btn" items={selectItems} title="Select">
          Select <Icon name="chevronDown" size={11} />
        </MenuButton>
        <MenuButton className="tb-btn" items={transformItems} title="Transform selected UVs">
          Transform <Icon name="chevronDown" size={11} />
        </MenuButton>
        <MenuButton className="tb-btn" items={uvMenuItems} title="Unwrap, project, pack and seams (U)">
          UV <Icon name="chevronDown" size={11} />
        </MenuButton>
        <button
          type="button"
          className={`tb-btn${overlays.background === 'checker' ? ' active' : ''}`}
          title="Checker grid background (shows stretching)"
          aria-pressed={overlays.background === 'checker'}
          onClick={toggleChecker}
        >
          <Icon name="grid" size={13} /> Checker
        </button>
        <label className="uv-inline" title="Stretch overlay: how much faces are distorted">
          Stretch
          <select value={overlays.stretch} onChange={(e) => useUv.getState().setOverlays({ stretch: e.target.value as StretchOverlay })}>
            <option value="off">Off</option>
            <option value="angle">Angle</option>
            <option value="area">Area</option>
          </select>
        </label>
        <span className="uv-spacer" />
        <span className="uv-part muted" title={texture.path ? `Texture: ${texture.path}` : 'No base-color map on this material'}>
          {src.kind === 'none' ? '' : src.path}
          {src.kind === 'generated' ? ' · read-only' : ''}
        </span>
        <button type="button" className={`tb-btn${sidebar ? ' active' : ''}`} title={`Sidebar (${keyHint('uv.sidebar')})`} aria-pressed={sidebar} onClick={() => useUv.getState().toggleSidebar()}>
          <Icon name="panelRight" size={13} />
        </button>
      </div>
      <div className="uv-body">
        <div className="uv-main">
          {src.kind === 'none' ? (
            <EmptyState icon="grid" title="UV Editor">
              {src.message}
            </EmptyState>
          ) : (
            <UvViewport
              layout={layout}
              image={texture.image}
              imageSize={texture.size}
              apiRef={apiRef}
              onCommit={(uvs) => {
                useUv.getState().setStatus(null);
                const s = latest.current.src;
                const model = useModel.getState();
                if (s.kind === 'mesh' && model.doc) model.update(withMeshUvs(model.doc, s.index, { uvs }));
              }}
              onContextMenu={(x, y) => setMenu({ x, y, items: contextItems() })}
            />
          )}
          {modal ? (
            <div className="uv-hint" role="status">
              <strong>{modal.text}</strong>
              <span className="muted">X / Y axis · Ctrl snap · type a value · Enter or click to confirm · Esc or right-click to cancel</span>
            </div>
          ) : src.kind === 'mesh' && !layout ? (
            <div className="uv-hint" role="status">
              <span>This mesh has no UVs yet.</span>
              <button type="button" className="tb-btn" onClick={() => actions.op('smartProject')}>
                Smart UV Project…
              </button>
              <button type="button" className="tb-btn" onClick={() => actions.op('unwrapAngle')}>
                Unwrap
              </button>
            </div>
          ) : src.kind === 'generated' ? (
            <div className="uv-hint" role="status">
              <span>{layout ? 'Generated UVs (read-only).' : 'Waiting for the engine’s UV layout…'} Convert to Mesh, or add an Unwrap op, to edit them.</span>
              <button type="button" className="tb-btn" onClick={actions.convertToMesh}>
                Convert to Mesh
              </button>
              <button type="button" className="tb-btn" onClick={actions.addUnwrapOp}>
                Add Unwrap op
              </button>
              {!layout && (
                <button type="button" className="tb-btn" onClick={() => useUv.getState().requestLayout(src.path)}>
                  <Icon name="refresh" size={12} />
                </button>
              )}
            </div>
          ) : null}
          {status && !modal && (
            <div className="uv-status" role="alert">
              <Icon name="info" size={12} />
              <span>{status}</span>
              <button type="button" className="uv-status-close" aria-label="Dismiss" onClick={() => useUv.getState().setStatus(null)}>
                <Icon name="x" size={11} />
              </button>
            </div>
          )}
          {layout && (
            <div className="uv-footer muted">
              {layout.islands.length} islands · {layout.faces.length} faces · {layout.uvs.length} UVs
              {selection.uvs.size ? ` · ${selection.uvs.size} selected` : ''} · {fmt(texelsPerMeter(density, textureSize), 0)} px/m at {textureSize}px
              {overlays.overlap ? ` · ${layout.overlap.length} overlapping` : ''}
            </div>
          )}
        </div>
        {sidebar && src.kind !== 'none' && (
          <aside className="uv-sidebar">
            <TransformCard editable={editable} center={center} count={indices.length} actions={actions} />
            <Card title="Texel Density" icon="grid">
              <PropRow label="Selection" hint={selFaces.length ? 'density of the selected faces' : 'density of all faces'}>
                <span className="uv-readout">
                  {fmt(texelsPerMeter(density, textureSize), 1)} px/m <span className="muted">({fmt(density, 3)} UV/m)</span>
                </span>
              </PropRow>
              <PropRow label="Texture size" hint="pixels across the texture (defaults to the base-color image)">
                <NumberField value={textureSize} min={1} max={16384} step={64} integer onChange={(v) => useUv.getState().setTextureSize(v)} />
              </PropRow>
              <PropRow label="Target" hint="texture pixels per meter">
                <NumberField value={targetDensity} min={0.01} step={8} onChange={(v) => useUv.getState().setTargetDensity(v)} />
              </PropRow>
              <div className="uv-buttons">
                <button type="button" className="tb-btn" onClick={actions.sampleDensity} title="Use the selection's density as the target">
                  Get
                </button>
                <button type="button" className="tb-btn" disabled={!editable} onClick={actions.setDensity} title="Scale each selected island to the target density">
                  Set
                </button>
              </div>
            </Card>
            <Card title="Pins & Seams" icon="target">
              <div className="uv-buttons">
                <button type="button" className="tb-btn" disabled={!editable} onClick={() => actions.pin(true)} title={`Pin (${keyHint('uv.pin')})`}>
                  Pin
                </button>
                <button type="button" className="tb-btn" disabled={!editable} onClick={() => actions.pin(false)} title={`Unpin (${keyHint('uv.unpin')})`}>
                  Unpin
                </button>
                <button type="button" className="tb-btn" disabled={!layout} onClick={actions.selectPinned}>
                  Select pinned
                </button>
              </div>
              <div className="uv-buttons">
                <button type="button" className="tb-btn" disabled={!editable} onClick={() => actions.seam(false)}>
                  Mark seam
                </button>
                <button type="button" className="tb-btn" disabled={!editable} onClick={() => actions.seam(true)}>
                  Clear seam
                </button>
                <button type="button" className="tb-btn" disabled={!editable} onClick={actions.seamsFromIslands}>
                  From islands
                </button>
              </div>
            </Card>
          </aside>
        )}
      </div>
      {menu && <ContextMenu x={menu.x} y={menu.y} items={menu.items} onClose={() => setMenu(null)} />}
      {dialog && (
        <UvOpDialog
          def={uvOp(dialog)}
          initial={opParams(uvOp(dialog), useUv.getState().params[dialog])}
          onClose={() => setDialog(null)}
          onRun={(params) => {
            const id = dialog;
            setDialog(null);
            actions.op(id, params);
          }}
        />
      )}
    </div>
  );
}

/** Keys of the engine operators (Blender's UV editor keymap). */
const OP_KEYS: Partial<Record<UvOpId, string[]>> = {
  pack: ['Ctrl+P'],
  averageScale: ['Ctrl+A'],
  minimizeStretch: ['Ctrl+V'],
  stitch: ['V'],
};

type UvActions = {
  move: (du: number, dv: number) => void;
  rotate: (deg: number) => void;
  scale: (su: number, sv: number) => void;
  flip: (axis: 'u' | 'v') => void;
};

/** Numeric transform of the selection: offsets / angle / factors applied with a button (one undo step each). */
function TransformCard({ editable, center, count, actions }: { editable: boolean; center: Vec2 | null; count: number; actions: UvActions }) {
  const [move, setMove] = useState<Vec2>([0, 0]);
  const [angle, setAngle] = useState(90);
  const [scale, setScale] = useState<Vec2>([1, 1]);
  const disabled = !editable || count === 0;
  return (
    <Card title="Transform" icon="move" subtitle={count ? `${count} UVs` : undefined}>
      <PropRow label="Center" hint="bounding-box center of the selection">
        <span className="uv-readout">{center ? `${fmt(center[0], 4)}, ${fmt(center[1], 4)}` : '—'}</span>
      </PropRow>
      <PropRow label="Move">
        <div className="uv-pair">
          <NumberField label="U" axis="x" value={move[0]} step={0.01} onChange={(v) => setMove([v, move[1]])} />
          <NumberField label="V" axis="y" value={move[1]} step={0.01} onChange={(v) => setMove([move[0], v])} />
          <button type="button" className="tb-btn" disabled={disabled} onClick={() => actions.move(move[0], move[1])}>
            Apply
          </button>
        </div>
      </PropRow>
      <PropRow label="Rotate (°)">
        <div className="uv-pair">
          <NumberField value={angle} step={1} onChange={setAngle} />
          <button type="button" className="tb-btn" disabled={disabled} onClick={() => actions.rotate(angle)}>
            Apply
          </button>
        </div>
      </PropRow>
      <PropRow label="Scale">
        <div className="uv-pair">
          <NumberField label="U" axis="x" value={scale[0]} step={0.05} onChange={(v) => setScale([v, scale[1]])} />
          <NumberField label="V" axis="y" value={scale[1]} step={0.05} onChange={(v) => setScale([scale[0], v])} />
          <button type="button" className="tb-btn" disabled={disabled} onClick={() => actions.scale(scale[0], scale[1])}>
            Apply
          </button>
        </div>
      </PropRow>
      <div className="uv-buttons">
        <button type="button" className="tb-btn" disabled={disabled} onClick={() => actions.flip('u')} title="Mirror along U">
          Flip U
        </button>
        <button type="button" className="tb-btn" disabled={disabled} onClick={() => actions.flip('v')} title="Mirror along V">
          Flip V
        </button>
        <button type="button" className="tb-btn" disabled={disabled} onClick={() => actions.rotate(90)} title="Rotate 90° counter-clockwise">
          <Icon name="undo" size={12} /> 90°
        </button>
        <button type="button" className="tb-btn" disabled={disabled} onClick={() => actions.rotate(-90)} title="Rotate 90° clockwise">
          <Icon name="redo" size={12} /> 90°
        </button>
      </div>
    </Card>
  );
}

/** "View › UV Editor": show the panel (beside the documents when it was closed). */
registerCommands({
  id: 'view.uv-editor',
  label: 'UV Editor',
  category: 'View',
  icon: 'grid',
  keywords: 'uv unwrap texture layout panel',
  run: () => useLayout.getState().show('uv'),
});
